import { useEffect, useState } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { LockKey, CheckCircle, XCircle, Trash, FloppyDisk, ShareNetwork, ClipboardText, DownloadSimple } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "drs_admin_token";
const TABS = ["pending", "approved", "rejected"];

// Ready-to-paste caption for Instagram/Facebook posts of approved guest photos.
const socialCaption = ({ pet_names, uploader_name, comment }) => {
  const firstLine = comment ? `"${comment}" — ${pet_names} 🐾` : `${pet_names} 🐾`;
  return `${firstLine}

Shared by ${uploader_name}, a guest at Double Rainbow Suites — pet-friendly stays in Wilton Manors & Fort Lauderdale.

📸 Instagram @doublerainbowsuites · Facebook: Double Rainbow Suites
🌐 doublerainbowsuites.com/fort-lauderdale-pet-friendly-vacation-rentals

#DoubleRainbowSuites #WiltonManors #FortLauderdale #PetFriendly #PetFriendlyTravel`;
};

const inputCls =
  "w-full bg-white border border-emerald/15 rounded-lg px-3 py-2 text-sm text-emerald focus:outline-none focus:ring-1 focus:ring-sunset";

function AdminPhotoCard({ photo, token, onChange }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [petNames, setPetNames] = useState(photo.pet_names);
  const [uploaderName, setUploaderName] = useState(photo.uploader_name);
  const [comment, setComment] = useState(photo.comment);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let url;
    fetch(`${API}${photo.image_url}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b) { url = URL.createObjectURL(b); setImgUrl(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [photo.image_url, token]);

  const act = async (body) => {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/pet-photos/${photo.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/pet-photos/${photo.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const dirty =
    petNames !== photo.pet_names || uploaderName !== photo.uploader_name || comment !== photo.comment;

  const copyCaption = async () => {
    const text = socialCaption({ pet_names: petNames, uploader_name: uploaderName, comment });
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div data-testid={`admin-photo-card-${photo.id}`} className="rounded-2xl border border-emerald/12 bg-white p-4 flex flex-col gap-4">
      {imgUrl ? (
        <img src={imgUrl} alt={photo.pet_names} className="w-full h-48 object-cover rounded-xl" />
      ) : (
        <div className="w-full h-48 rounded-xl bg-sand animate-pulse" />
      )}
      <div className="space-y-3">
        <div>
          <label className="overline block mb-1">Pet name(s)</label>
          <input className={inputCls} data-testid={`admin-pet-names-${photo.id}`} value={petNames} maxLength={80} onChange={(e) => setPetNames(e.target.value)} />
        </div>
        <div>
          <label className="overline block mb-1">Shared by</label>
          <input className={inputCls} data-testid={`admin-uploader-${photo.id}`} value={uploaderName} maxLength={60} onChange={(e) => setUploaderName(e.target.value)} />
          {photo.uploader_email && (
            <p className="text-xs text-emerald-soft/70 mt-1" data-testid={`admin-email-${photo.id}`}>
              {photo.uploader_email}
              {photo.approval_email_sent ? " · approval email sent" : " · will be emailed on approval"}
            </p>
          )}
        </div>
        <div>
          <label className="overline block mb-1">Note from the pet</label>
          <textarea rows={2} className={inputCls} data-testid={`admin-comment-${photo.id}`} value={comment} maxLength={280} onChange={(e) => setComment(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-auto">
        {dirty && (
          <button onClick={() => act({ pet_names: petNames, uploader_name: uploaderName, comment })}
            disabled={busy} data-testid={`admin-save-${photo.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-emerald text-cream disabled:opacity-50">
            <FloppyDisk size={14} weight="bold" /> Save edits
          </button>
        )}
        {photo.status !== "approved" && (
          <button onClick={() => act({ status: "approved" })} disabled={busy}
            data-testid={`admin-approve-${photo.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-sage/20 text-emerald border border-sage/40 disabled:opacity-50">
            <CheckCircle size={14} weight="fill" /> Approve
          </button>
        )}
        {photo.status !== "rejected" && (
          <button onClick={() => act({ status: "rejected" })} disabled={busy}
            data-testid={`admin-reject-${photo.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-sunset/10 text-sunset border border-sunset/30 disabled:opacity-50">
            <XCircle size={14} weight="fill" /> Reject
          </button>
        )}
        <button onClick={remove} disabled={busy} data-testid={`admin-delete-${photo.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full text-emerald-soft/70 border border-emerald/15 disabled:opacity-50">
          <Trash size={14} weight="bold" /> Delete
        </button>
        {photo.status === "approved" && (
          <button onClick={() => setShareOpen((v) => !v)} data-testid={`admin-share-${photo.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-sunset/10 text-sunset border border-sunset/30 hover:bg-sunset/20 transition-colors">
            <ShareNetwork size={14} weight="bold" /> Share to socials
          </button>
        )}
      </div>
      {shareOpen && photo.status === "approved" && (
        <div data-testid={`admin-share-panel-${photo.id}`} className="border border-emerald/10 rounded-xl bg-sand/60 p-3 space-y-2.5">
          <p className="text-xs text-emerald-soft/80 leading-relaxed">
            Post in ~30 seconds: copy the caption, download the photo, then start a new post in the Instagram or Facebook app and paste.
          </p>
          <textarea readOnly rows={8} className={`${inputCls} text-xs`} data-testid={`admin-caption-${photo.id}`}
            value={socialCaption({ pet_names: petNames, uploader_name: uploaderName, comment })}
            onFocus={(e) => e.target.select()} />
          <div className="flex flex-wrap gap-2">
            <button onClick={copyCaption} data-testid={`admin-copy-caption-${photo.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-emerald text-cream">
              <ClipboardText size={14} weight="bold" /> {copied ? "Copied!" : "Copy caption"}
            </button>
            <a href={`${API}/api/pet-photos/${photo.id}/download`} download
              data-testid={`admin-download-photo-${photo.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-emerald/25 text-emerald hover:border-sunset hover:text-sunset transition-colors">
              <DownloadSimple size={14} weight="bold" /> Download photo
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPetPhotos() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [photos, setPhotos] = useState([]);
  const [tab, setTab] = useState("pending");
  const [authFailed, setAuthFailed] = useState(false);

  const load = async (tok) => {
    const res = await fetch(`${API}/api/admin/pet-photos`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    if (res.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setAuthFailed(true);
      return;
    }
    const d = await res.json();
    setPhotos(d.photos || []);
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    const res = await fetch(`${API}/api/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      setLoginError("Invalid password");
      return;
    }
    const d = await res.json();
    sessionStorage.setItem(TOKEN_KEY, d.token);
    setAuthFailed(false);
    setToken(d.token);
  };

  const counts = Object.fromEntries(TABS.map((t) => [t, photos.filter((p) => p.status === t).length]));
  const visible = photos.filter((p) => p.status === tab);

  return (
    <div className="min-h-screen bg-cream text-emerald">
      <Seo title="Admin — Pet photos | Double Rainbow Suites" noindex />
      <Navbar />
      <main className="pt-36 md:pt-44 pb-28 px-6">
        <div className="max-w-5xl mx-auto">
          {!token ? (
            <div className="max-w-sm mx-auto text-center">
              <LockKey size={36} weight="duotone" className="text-sunset mx-auto mb-6" />
              <h1 className="font-display text-3xl tracking-tight mb-6">Pet photo review</h1>
              {authFailed && <p className="text-sm text-sunset mb-4">Session expired — please sign in again.</p>}
              <form onSubmit={login} className="space-y-4">
                <input type="password" data-testid="admin-login-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Admin password"
                  className="w-full bg-white border border-emerald/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-sunset" />
                {loginError && <p data-testid="admin-login-error" className="text-sm text-sunset">{loginError}</p>}
                <button type="submit" data-testid="admin-login-submit" className="btn-primary w-full justify-center">
                  Sign in
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                  <p className="overline text-sunset mb-2">Admin</p>
                  <h1 className="font-display text-3xl md:text-4xl tracking-tight">Pet photo review</h1>
                </div>
                <button onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); }}
                  data-testid="admin-logout" className="text-xs uppercase tracking-[0.18em] text-emerald-soft/60 hover:text-emerald">
                  Sign out
                </button>
              </div>
              <div className="flex gap-2 mb-8">
                {TABS.map((t) => (
                  <button key={t} onClick={() => setTab(t)} data-testid={`admin-filter-${t}`}
                    className={`text-xs font-semibold uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors ${
                      tab === t ? "bg-emerald text-cream border-emerald" : "border-emerald/15 text-emerald-soft hover:border-emerald/40"
                    }`}>
                    {t} ({counts[t] || 0})
                  </button>
                ))}
              </div>
              {visible.length === 0 ? (
                <p data-testid="admin-empty" className="text-emerald-soft font-light">No {tab} photos right now.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {visible.map((p) => (
                    <AdminPhotoCard key={p.id} photo={p} token={token} onChange={() => load(token)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
